import { useState, useEffect } from 'react';
import { Table, Input, Row, Col, Spinner, Label } from 'reactstrap';
import { Edit, Trash2, Plus, RefreshCw } from 'react-feather';
import { toast } from 'react-toastify';
import { Btn } from '../../../AbstractElements';
import CountryService from '../../../Api/services/country.service';
import type { components } from '../../../Api/types/openapi';
import CountryFormModal from './CountryFormModal';
import DeleteConfirmModal from '../Roles/DeleteConfirmModal';
import { useTranslation } from 'react-i18next';

type CountryResponse = components['schemas']['CountryResponse'];
type PaginationCountryResponse = components['schemas']['Pagination_CountryResponse_'];

const CountriesList = () => {
  const { t } = useTranslation();
  const [countries, setCountries] = useState<CountryResponse[]>([]);
  const [pagination, setPagination] = useState<PaginationCountryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryResponse | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadCountries();
  }, [currentPage, searchTerm]);

  const loadCountries = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        limit,
      };

      if (searchTerm) {
        params.name = searchTerm;
      }

      const data = await CountryService.getCountriesPaginated(params);
      
      if (!data || !Array.isArray(data.results)) {
        console.warn('Respuesta inválida del servidor: results no es un array');
        setCountries([]);
        setPagination(null);
        return;
      }
      
      setCountries(data.results);
      setPagination(data);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al cargar países');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedCountry(null);
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEdit = (country: CountryResponse) => {
    setSelectedCountry(country);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDelete = async (country: CountryResponse) => {
    if (!country.id) return;
    
    try {
      // Verificar si se puede eliminar
      await CountryService.checkDeleteCountry(country.id);
      setSelectedCountry(country);
      setShowDeleteModal(true);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('No se puede eliminar este país');
      toast.error(errorMessage);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCountry?.id) return;

    try {
      await CountryService.deleteCountry(selectedCountry.id);
      toast.success(t('País eliminado exitosamente'));
      setShowDeleteModal(false);
      setSelectedCountry(null);
      loadCountries();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al eliminar país');
      toast.error(errorMessage);
    }
  };

  const handleToggleState = async (country: CountryResponse) => {
    if (!country.id) return;

    try {
      await CountryService.changeCountryState(country.id);
      toast.success(t('Estado del país actualizado'));
      loadCountries();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al cambiar estado');
      toast.error(errorMessage);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div>
      <Row className="mb-3">
        <Col md="6">
          <Input
            type="text"
            placeholder={t('Buscar por nombre...')}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </Col>
        <Col md="6" className="text-end">
          <Btn color="primary" onClick={handleCreate} className="me-2">
            <Plus size={16} className="me-1" />
            {t('Nuevo País')}
          </Btn>
          <Btn color="secondary" onClick={loadCountries}>
            <RefreshCw size={16} className="me-1" />
            {t('Actualizar')}
          </Btn>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
        </div>
      ) : (
        <>
          <Table responsive striped>
            <thead>
              <tr>
                <th>{t('ID')}</th>
                <th>{t('Nombre')}</th>
                <th>{t('Código')}</th>
                <th>{t('Estado')}</th>
                <th>{t('Acciones')}</th>
              </tr>
            </thead>
            <tbody>
              {countries.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center">
                    {t('No se encontraron países')}
                  </td>
                </tr>
              ) : (
                countries.map((country) => (
                  <tr key={country.id}>
                    <td>{country.id}</td>
                    <td>{country.name}</td>
                    <td>
                      <code>{country.code || '-'}</code>
                    </td>
                    <td>
                      <div className="form-check form-switch">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          checked={country.active}
                          onChange={() => handleToggleState(country)}
                        />
                      </div>
                    </td>
                    <td>
                      <Btn
                        color="primary"
                        size="sm"
                        className="me-1 btn_list_action"
                        onClick={() => handleEdit(country)}
                      >
                        <Edit size={14} />
                      </Btn>
                      <Btn
                        color="danger"
                        size="sm"
                        className="btn_list_action"
                        onClick={() => handleDelete(country)}
                      >
                        <Trash2 size={14} />
                      </Btn>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {pagination && pagination.pages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                {t('Página')} {pagination.current_page} {t('de')} {pagination.pages}
              </div>
              <div>
                <Btn
                  color="primary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  className="me-2"
                >
                  {t('Anterior')}
                </Btn>
                <Btn
                  color="primary"
                  size="sm"
                  disabled={currentPage === pagination.pages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  {t('Siguiente')}
                </Btn>
              </div>
            </div>
          )}
        </>
      )}

      <CountryFormModal
        isOpen={showModal}
        toggle={() => setShowModal(false)}
        country={selectedCountry}
        isEditing={isEditing}
        onSuccess={loadCountries}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        toggle={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('Eliminar País')}
        message={t('¿Estás seguro de que deseas eliminar este país?')}
      />
    </div>
  );
};

export default CountriesList;

